import type { FC, HTMLAttributes, ReactElement, ReactNode } from 'react';

import type { TrophyDisplayData } from '../../../store/slices/promotions/types';
import { SessionTimer } from '../../compliance/SessionTimer';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { L12nText } from '../../text/L12nText';
import { Time } from '../../time';
import { Tooltip } from '../../tooltip/Tooltip';

import styles from './gameFooter.module.css';

interface SessionIds {
  aamsSessionId: string;
  aamsTicket: string;
}

export interface GameFooterProps {
  balance: string;
  bonus: ReactNode;
  gameId: string;
  net: ReactNode;
  sessionIDs: SessionIds | null;
  showSessionTimer: boolean;
  tableBetLimits: [ReactNode, ReactNode];
  tableName: ReactNode;
  totalBet: ReactNode;
  trophy: TrophyDisplayData;
}

export const GameFooter: FC<GameFooterProps> = ({
  balance,
  bonus,
  gameId,
  net,
  sessionIDs,
  showSessionTimer,
  tableBetLimits,
  tableName,
  totalBet,
  trophy,
}): ReactElement => {
  // TODO: Rework this solution to avoid element doubling

  return (
    <div className={styles.gameFooter}>
      <div className={styles.gameFooter__content}>
        {/*Small variant for portrait*/}
        <div data-testid="game-footer-container" className={styles.gameFooter__contentContainer}>
          <div className={styles.gameFooter__contentMain}>
            <div>
              {tableName} {tableBetLimits[0]} - {tableBetLimits[1]}
            </div>
            <div data-testid="game-footer-info" style={{ textAlign: 'right' }}>
              <L12nText tk="GAME_NUMBER" />:{gameId} <Time />
            </div>
          </div>
          <div className={styles.gameFooter__contentRow}>
            <Balance balance={balance} />
            <TotalBet trophy={trophy}>{totalBet}</TotalBet>
          </div>
          <div className={styles.gameFooter__contentHeader}>
            {bonus ? <BonusBalance>{bonus}</BonusBalance> : null}
            {net !== null ? <NetPosition>{net}</NetPosition> : null}
            {showSessionTimer ? (
              <div>
                <SessionTimer />
              </div>
            ) : null}
          </div>
        </div>

        {/*Big variant for landscape*/}
        <div data-testid="game-footer-container-big" className={styles.gameFooter__contentContainer_big}>
          <div className={styles.gameFooter__contentMain}>
            <div>
              {tableName} {tableBetLimits[0]} - {tableBetLimits[1]}
            </div>
            <div data-testid="game-footer-info">
              {showSessionTimer ? (
                <div>
                  <SessionTimer />
                </div>
              ) : null}{' '}
              <L12nText tk="GAME_NUMBER" />:{gameId} <Time />
            </div>
          </div>

          <div className={styles.gameFooter__contentRow}>
            <Balance balance={balance} />
            {bonus ? <BonusBalance>{bonus}</BonusBalance> : null}
            {net !== null ? <NetPosition>{net}</NetPosition> : null}
            <TotalBet trophy={trophy}>{totalBet}</TotalBet>
          </div>
        </div>

        {sessionIDs ? (
          <div className={styles.gameFooter__contentHeader}>
            <SessionId aamsSessionId={sessionIDs.aamsSessionId} aamsTicket={sessionIDs.aamsTicket} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const SessionId: FC<SessionIds> = ({ aamsTicket, aamsSessionId }): ReactElement => {
  return (
    <>
      <span key="aamsSessionId">ID-S: {aamsSessionId}</span>
      <span key="aamsTicket">ID-P: {aamsTicket}</span>
    </>
  );
};

const BonusBalance: FC<HTMLAttributes<HTMLDivElement>> = ({ children }): ReactElement => {
  return (
    <div>
      <span className={styles.gameFooter__label}>
        <L12nText tk="BONUS_BALANCE" />
      </span>{' '}
      <span className={styles.gameFooter__label_acent}>{children}</span>
    </div>
  );
};

const NetPosition: FC<HTMLAttributes<HTMLDivElement>> = ({ children }): ReactElement => {
  return (
    <div>
      <span className={styles.gameFooter__label}>
        <L12nText tk="NET_POSITION" />
      </span>{' '}
      <span className={styles.gameFooter__label_acent}>{children}</span>
    </div>
  );
};

interface BalanceProps {
  balance: string;
}

const Balance: FC<BalanceProps> = ({ balance }): ReactElement => {
  return (
    <div>
      <span className={styles.gameFooter__label}>
        <L12nText tk="BALANCE" />
      </span>{' '}
      {balance.length > 0 && (
        <span data-testid="footer-balance" className={styles.gameFooter__label_acent}>
          {isNaN(Number(balance)) ? balance : <CurrencyConnected value={Number(balance)} />}
        </span>
      )}
    </div>
  );
};

interface TotalBetWingProps {
  children: ReactNode;
  trophy: TrophyDisplayData;
}

const TotalBet: FC<TotalBetWingProps> = ({ trophy, children }): ReactElement => {
  let trophyIcon = <></>;
  switch (trophy?.visibility) {
    case 'active':
      trophyIcon = <span className={styles.gameFooter__label_trophyActive}></span>;
      break;
    case 'inactive':
      trophyIcon = (
        <Tooltip
          attrs={[trophy.minBet]}
          displayOnTouch
          displayTimer={5000}
          displayTrigger={trophy.showTrophyTooltip}
          style={{ display: 'inline-block' }}
          text={trophy.isRoundLimitExceed ? 'DAILY_TOURNAMENT_LIMIT' : 'PROMO_BET_BELOWMIN'}
        >
          <span className={styles.gameFooter__label_trophyInactive}></span>
        </Tooltip>
      );
      break;
  }
  return (
    <div className={styles.gameFooter__container}>
      <span className={`${styles.gameFooter__label}`}>
        {trophyIcon} <L12nText tk="BET" />
      </span>{' '}
      <span data-testid="footer-total-bet" className={styles.gameFooter__label_acent}>
        {children}
      </span>
    </div>
  );
};
